import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../context/TranslationContext';
import { getAdminOrders, getAdminOrdersByDateRange, updateOrderStatus, deleteOrder } from '../../services/api';
import { Order } from '../../types';
import { formatDate } from '../../utils/dateUtils';
import './OrderListPage.css';

const OrderListPage: React.FC = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [groupedOrders, setGroupedOrders] = useState<Record<string, Order[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isFiltering, setIsFiltering] = useState<boolean>(false);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchOrders = async () => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      
      const orders = await getAdminOrders(token);
      setGroupedOrders(orders);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersByDateRange = async () => {
    if (!token || !startDate || !endDate) return;

    try {
      setLoading(true);
      setError(null);
      setIsFiltering(true);
      
      const orders = await getAdminOrdersByDateRange(startDate, endDate, token);
      setGroupedOrders(orders);
    } catch (err) {
      console.error('Error fetching orders by date range:', err);
      setError('Failed to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setStartDate('');
    setEndDate('');
    setIsFiltering(false);
    fetchOrders();
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    if (!token) return;
    
    // Check if orderId is valid
    if (!orderId) {
      setError('Cannot update order: Invalid order ID');
      return;
    }

    try {
      await updateOrderStatus(orderId, newStatus, token);
      
      // Refresh orders after status update
      if (isFiltering && startDate && endDate) {
        fetchOrdersByDateRange();
      } else {
        fetchOrders();
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Failed to update order status. Please try again.');
    }
  };

  const handleDeleteOrder = async (orderId: string | number) => {
    if (!token) return;
    
    // Validate that we have a valid order ID
    if (!orderId) {
      setError('Cannot delete order: Invalid order ID');
      return;
    }
    
    if (window.confirm(t('admin.orders.confirmDelete'))) {
      try {
        await deleteOrder(orderId.toString(), token);
        
        // Refresh orders after deletion
        if (isFiltering && startDate && endDate) {
          fetchOrdersByDateRange();
        } else {
          fetchOrders();
        }
      } catch (err) {
        console.error('Error deleting order:', err);
        setError('Failed to delete order. Please try again.');
      }
    }
  };

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'confirmed':
        return 'status-confirmed';
      case 'completed':
        return 'status-completed';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  if (loading) {
    return <div className="orders-loading">{t('admin.common.loading')}</div>;
  }

  if (error) {
    return <div className="orders-error">{error}</div>;
  }

  return (
    <div className="orders-list-page">
      <h1>{t('admin.orders.title')}</h1>
      
      <div className="orders-filter">
        <div className="filter-inputs">
          <div className="filter-group">
            <label htmlFor="startDate">{t('admin.orders.startDate')}</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label htmlFor="endDate">{t('admin.orders.endDate')}</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
        
        <div className="filter-actions">
          <button 
            className="filter-button"
            onClick={fetchOrdersByDateRange}
            disabled={!startDate || !endDate}
          >
            {t('admin.orders.filter')}
          </button>
          
          {isFiltering && (
            <button 
              className="reset-button"
              onClick={resetFilters}
            >
              {t('admin.orders.resetFilters')}
            </button>
          )}
        </div>
      </div>
      
      {Object.keys(groupedOrders).length === 0 ? (
        <div className="no-orders">{t('admin.orders.noOrders')}</div>
      ) : (
        Object.entries(groupedOrders)
          .sort(([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime())
          .map(([date, orders]) => (
            <div key={date} className="orders-date-group">
              <h2 className="date-header">{formatDate(new Date(date))}</h2>
              
              <div className="orders-table-container">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>{t('admin.orders.orderId')}</th>
                      <th>{t('admin.orders.customerName')}</th>
                      <th>{t('admin.orders.customerPhone')}</th>
                      <th>{t('admin.orders.pickupLocation')}</th>
                      <th>{t('admin.orders.items')}</th>
                      <th>{t('admin.orders.totalAmount')}</th>
                      <th>{t('admin.orders.status')}</th>
                      <th>{t('admin.orders.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order._id}>
                        <td>{order.orderId}</td>
                        <td>{order.customerName}</td>
                        <td>{order.customerPhone}</td>
                        <td>
                          {order.pickupLocation ? 
                            (typeof order.pickupLocation === 'object' ? 
                              order.pickupLocation.name : 
                              'ID: ' + order.pickupLocation
                            ) : 
                            t('admin.orders.noPickupLocation')
                          }
                        </td>
                        <td>
                          {order.items && order.items.length > 0 ? (
                            <ul className="order-items-list">
                              {order.items.map((item, index) => (
                                <li key={index}>
                                  <strong>
                                    {item.productName || (typeof item.product === 'object' ? item.product.name : 'Unknown')}
                                  </strong>
                                  <span> - {item.quantity} db</span>
                                  <div className="item-price">
                                    {item.productPrice ? `${item.productPrice.toLocaleString()} Ft/db` : ''}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="no-items">{t('admin.orders.noItems')}</span>
                          )}
                        </td>
                        <td>{order.totalAmount.toLocaleString()} Ft</td>
                        <td>
                          <select
                            className={`status-select ${getStatusClass(order.status || 'pending')}`}
                            value={order.status || 'pending'}
                            onChange={(e) => {
                              // Find the first valid ID to use
                              const idToUse = order.id || order._id;
                              if (idToUse) {
                                handleStatusChange(idToUse.toString(), e.target.value);
                              } else {
                                setError('Cannot update order: Missing ID');
                              }
                            }}
                          >
                            <option value="pending">{t('admin.orders.statuses.pending')}</option>
                            <option value="confirmed">{t('admin.orders.statuses.confirmed')}</option>
                            <option value="completed">{t('admin.orders.statuses.completed')}</option>
                            <option value="cancelled">{t('admin.orders.statuses.cancelled')}</option>
                          </select>
                        </td>
                        <td>
                          <button
                            className="delete-button"
                            onClick={() => {
                              // Find the first valid ID to use
                              const idToUse = order.id || order._id || order.orderId;
                              if (idToUse) {
                                handleDeleteOrder(idToUse);
                              } else {
                                setError('Cannot delete order: Missing ID');
                              }
                            }}
                          >
                            {t('admin.common.delete')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
      )}
    </div>
  );
};

export default OrderListPage;
